import { useState, useEffect } from "react";
import type { ColumnVisibility } from "@/types/transaction";

interface ColumnConfigState {
  visibility: ColumnVisibility;
  order: string[];
}

const DEFAULT_VISIBILITY: ColumnVisibility = {
  referenceNumber: true,
  transactionDate: true,
  customerName: false,
  transactionType: true,
  amount: true,
  outstandingAmount: true,
  status: true,
  description: false,
  createdBy: false,
  modifiedBy: false,
  dueDate: true,
  actions: false,
  accountCode: false,
  netAmount: false,
  vatAmount: false,
  customerReference: false,
  periodNumber: false,
  year: false,
  onDispute: false,
  subLedger: false,
  orderNumber: false,
};

const DEFAULT_ORDER: string[] = [
  "referenceNumber",
  "transactionDate",
  "dueDate",
  "transactionType",
  "amount",
  "outstandingAmount",
  "status",
  "description",
  "accountCode",
  "netAmount",
  "vatAmount",
  "customerReference",
  "periodNumber",
  "year",
  "onDispute",
  "subLedger",
  "orderNumber",
  "createdBy",
  "modifiedBy",
  "customerName",
  "actions",
];

export function useColumnConfig(storageKey: string = "transaction-column-config") {
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(DEFAULT_VISIBILITY);
  const [columnOrder, setColumnOrder] = useState<string[]>(DEFAULT_ORDER);

  // Load configuration from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const config: ColumnConfigState = JSON.parse(stored);
        setColumnVisibility(config.visibility);
        setColumnOrder(config.order);
      }
    } catch (error) {
      console.error("Error loading column configuration:", error);
    }
  }, [storageKey]);

  // Save configuration to localStorage
  const saveConfig = (visibility: ColumnVisibility, order: string[]) => {
    try {
      const config: ColumnConfigState = { visibility, order };
      localStorage.setItem(storageKey, JSON.stringify(config));
      setColumnVisibility(visibility);
      setColumnOrder(order);
    } catch (error) {
      console.error("Error saving column configuration:", error);
    }
  };

  // Reset to default configuration
  const resetConfig = () => {
    try {
      localStorage.removeItem(storageKey);
      setColumnVisibility(DEFAULT_VISIBILITY);
      setColumnOrder(DEFAULT_ORDER);
    } catch (error) {
      console.error("Error resetting column configuration:", error);
    }
  };

  return {
    columnVisibility,
    columnOrder,
    saveConfig,
    resetConfig,
  };
}
